import React from "react";
import { Mic, X, Radio } from "lucide-react";

const BAR_DELAYS = [0, 120, 240, 360, 480, 360, 240, 120, 0];

export const ClaraListening = ({
    title = "Clara is listening",
    subtitle,
    onCancel,
    primaryAction,
    primaryLabel,
    primaryTestId = "clara-listening-primary",
    examples,
    liveTranscript,
    isLive = false,
    demoCommands,
}) => {
    return (
        <section
            role="dialog"
            aria-modal="true"
            aria-label={title}
            data-testid="clara-listening-screen"
            className="rounded-3xl bg-gradient-to-b from-[#0A3D7A] to-[#082F5E] text-white p-7 sm:p-9 shadow-xl fade-in-up relative overflow-hidden"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.07]"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "14px 14px",
                }}
            />
            <div className="relative">
                <p className="text-xs tracking-[0.32em] font-semibold text-[#E0C875] uppercase">
                    Voice Input
                </p>
                <h2 className="font-display text-3xl sm:text-4xl font-bold mt-1">
                    {title}
                </h2>
                {subtitle && (
                    <p className="text-white/85 mt-3 text-base sm:text-lg leading-relaxed">
                        {subtitle}
                    </p>
                )}

                {/* Pulse + voice bars */}
                <div className="mt-8 mb-2 flex items-center justify-center relative h-36">
                    <span
                        aria-hidden="true"
                        className={`absolute w-28 h-28 rounded-full bg-[#B8860B]/40 ${
                            isLive ? "pulse-ring" : ""
                        }`}
                    />
                    <span
                        aria-hidden="true"
                        className={`absolute w-28 h-28 rounded-full bg-[#B8860B]/30 ${
                            isLive ? "pulse-ring" : ""
                        }`}
                        style={{ animationDelay: "0.6s" }}
                    />
                    <div className="relative w-24 h-24 rounded-full bg-[#B8860B] flex items-center justify-center shadow-2xl">
                        <Mic
                            className="w-11 h-11 text-[#1A1A2E]"
                            aria-hidden="true"
                            strokeWidth={2.5}
                        />
                    </div>
                </div>

                <div
                    className="flex items-end justify-center gap-2 h-16 mt-2"
                    aria-hidden="true"
                >
                    {BAR_DELAYS.map((d, i) => (
                        <span
                            key={`bar-${i}-${d}`}
                            className={`w-2.5 h-full rounded-full bg-white/90 ${
                                isLive ? "voice-bar" : "opacity-40"
                            }`}
                            style={isLive ? { animationDelay: `${d}ms` } : null}
                        />
                    ))}
                </div>

                {liveTranscript ? (
                    <div
                        className="mt-7 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-4"
                        data-testid="clara-live-transcript"
                        aria-live="polite"
                    >
                        <p className="text-xs tracking-[0.2em] font-semibold text-[#E0C875] uppercase mb-1.5">
                            You said
                        </p>
                        <p className="text-white text-lg leading-snug">
                            {liveTranscript}
                        </p>
                    </div>
                ) : (
                    examples &&
                    examples.length > 0 && (
                        <div className="mt-7">
                            <p className="text-xs tracking-[0.2em] font-semibold text-[#E0C875] uppercase mb-3">
                                Try saying
                            </p>
                            <ul className="space-y-2">
                                {examples.map((ex) => (
                                    <li
                                        key={ex}
                                        className="text-white/95 text-base bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/15"
                                    >
                                        “{ex}”
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )
                )}

                {demoCommands && demoCommands.length > 0 && (
                    <div className="mt-6 rounded-2xl bg-white/8 border border-white/15 p-3">
                        <p className="text-xs tracking-[0.2em] font-semibold text-[#E0C875] uppercase mb-2 px-1">
                            Or tap to simulate
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            {demoCommands.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={c.onClick}
                                    data-testid={`demo-voice-${c.id}`}
                                    className="min-h-[44px] rounded-xl bg-white text-[#0A3D7A] font-semibold text-sm px-3 py-2 hover:bg-[#FFF8F0] transition-colors flex items-center gap-1.5"
                                >
                                    <Radio className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                                    <span className="truncate">{c.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-7 space-y-3">
                    {primaryAction && (
                        <button
                            type="button"
                            onClick={primaryAction}
                            data-testid={primaryTestId}
                            className="w-full min-h-[56px] rounded-2xl bg-white text-[#0A3D7A] text-lg font-bold flex items-center justify-center gap-2 hover:bg-[#FFF8F0] transition-colors shadow-lg active:scale-[0.99]"
                        >
                            {primaryLabel || "I give permission"}
                        </button>
                    )}
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            data-testid="clara-listening-cancel"
                            className="w-full min-h-[48px] rounded-2xl border-2 border-white/40 text-white/95 text-base font-semibold flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
                        >
                            <X className="w-4 h-4" aria-hidden="true" />
                            Cancel
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ClaraListening;
